/**
 * Main dashboard plugin API.
 * Keeps plugin registration and realtime socket hooks in one place.
 */

import { markRaw } from 'vue';
import type { I18n } from 'vue-i18n';
import type {
  TabConfig,
  ComponentConfig,
  ProviderConfig,
  ContentOverrideConfig,
  ChannelTypeMapping,
  PluginSystemAPI,
  TabVisibilityRule,
} from './dashboard-types';
import { pluginRegistry, getReadonlyRegistry, clearRegistry } from './dashboard-registry';
import { log } from './dashboard-utils';
import { registerPluginTranslations } from './dashboard-translations';
import { getTabResolver } from './dashboard-tab-resolver';

const socketHookName = (eventName: string) => `socket:${eventName}`;

export const PluginSystem: PluginSystemAPI = {
  registerTab(componentName: string, config: TabConfig): void {
    if (!componentName || !config.key || !config.name) {
      console.error('[PLUGIN-SYSTEM] Invalid tab config:', {
        componentName,
        config,
      });
      return;
    }

    if (!pluginRegistry.tabs[componentName]) {
      pluginRegistry.tabs[componentName] = [];
    }

    const exists = pluginRegistry.tabs[componentName].some(
      tab => tab.key === config.key
    );

    if (!exists) {
      // Mark component as raw to prevent Vue from making it reactive
      const safeConfig = config.component ? { ...config, component: markRaw(config.component) } : config;
      pluginRegistry.tabs[componentName].push(safeConfig);
      log('✅ Tab registered:', componentName, config.key);
    } else {
      log('⚠️  Tab already registered:', componentName, config.key);
    }
  },

  registerComponent(componentName: string, config: ComponentConfig): void {
    if (!componentName || !config.name) {
      console.error('[PLUGIN-SYSTEM] Invalid component config:', {
        componentName,
        config,
      });
      return;
    }

    if (!pluginRegistry.components[componentName]) {
      pluginRegistry.components[componentName] = [];
    }

    // Mark component as raw to prevent Vue from making it reactive
    const safeConfig = { ...config, component: markRaw(config.component) };
    pluginRegistry.components[componentName].push(safeConfig);
    log('✅ Component registered:', componentName, config.name);
  },

  registerProvider(providerType: string, config: ProviderConfig): void {
    if (!providerType || !config.key || !config.component) {
      console.error('[PLUGIN-SYSTEM] Invalid provider config:', {
        providerType,
        config,
      });
      return;
    }

    if (!pluginRegistry.providers[providerType]) {
      pluginRegistry.providers[providerType] = [];
    }

    const exists = pluginRegistry.providers[providerType].some(
      p => p.key === config.key
    );

    if (!exists) {
      // Mark component as raw to prevent Vue from making it reactive
      const safeConfig = { ...config, component: markRaw(config.component) };
      pluginRegistry.providers[providerType].push(safeConfig);
      log('✅ Provider registered:', providerType, config.key);
    } else {
      log('⚠️  Provider already registered:', providerType, config.key);
    }
  },

  registerChannelType(channelType: string, i18nKey: string): void {
    if (!channelType || !i18nKey) {
      console.error('[PLUGIN-SYSTEM] Invalid channel type config:', {
        channelType,
        i18nKey,
      });
      return;
    }

    if (!pluginRegistry.hooks.channelTypes) {
      pluginRegistry.hooks.channelTypes = [];
    }

    pluginRegistry.hooks.channelTypes.push({ channelType, i18nKey });
    log('✅ Channel type registered:', channelType, '→', i18nKey);
  },

  registerTranslations(i18n: I18n, translations: Record<string, any>, locale: string = 'en'): void {
    registerPluginTranslations(i18n, translations, locale);
  },

  getChannelTypes(): ChannelTypeMapping[] {
    return (pluginRegistry.hooks.channelTypes as ChannelTypeMapping[]) || [];
  },

  registerHook(hookName: string, callback: (...args: any[]) => any): void {
    if (!hookName || typeof callback !== 'function') {
      console.error('[PLUGIN-SYSTEM] Invalid hook:', { hookName, callback });
      return;
    }

    if (!pluginRegistry.hooks[hookName]) {
      pluginRegistry.hooks[hookName] = [];
    }

    pluginRegistry.hooks[hookName].push(callback);
    log('✅ Hook registered:', hookName);
  },

  registerSocketEvent(eventName: string, callback: (...args: any[]) => any): void {
    if (!eventName || typeof callback !== 'function') {
      console.error('[PLUGIN-SYSTEM] Invalid socket event handler:', {
        eventName,
        callback,
      });
      return;
    }

    this.registerHook(socketHookName(eventName), callback);
    log('✅ Socket event registered:', eventName);
  },

  registerActionCableEvent(eventName: string, callback: (...args: any[]) => any): void {
    this.registerSocketEvent(eventName, callback);
  },

  getTabs(componentName: string, context: any = {}): TabConfig[] {
    const tabs = pluginRegistry.tabs[componentName] || [];
    
    const channelType = context.channel_type || context.inbox?.channel_type;
    const medium = context.medium || context.inbox?.medium;

    // Extract provider from context (can be in multiple places)
    const provider = context.provider ||
                    context.channel?.provider ||
                    context.channel?.additional_attributes?.provider ||
                    context.additional_attributes?.provider ||
                    context.inbox?.provider ||
                    context.inbox?.additional_attributes?.provider ||
                    context.inbox?.channel?.provider ||
                    context.inbox?.channel?.additional_attributes?.provider;

    return tabs.filter(tab => {
      // If tab has medium filter, check if it matches
      if (tab.medium && medium !== tab.medium) {
        return false;
      }
      
      // If tab has channelType filter, check if it matches
      if (tab.channelType && channelType !== tab.channelType) {
        return false;
      }
      
      // If tab has provider filter, check if it matches
      if (tab.provider && provider !== tab.provider) {
        return false;
      }

      return true;
    });
  },

  getComponents(componentName: string): ComponentConfig[] {
    return pluginRegistry.components[componentName] || [];
  },

  getProviders(providerType: string): ProviderConfig[] {
    return pluginRegistry.providers[providerType] || [];
  },

  registerContentOverride(componentName: string, config: ContentOverrideConfig): void {
    if (!componentName || !config.tabKey || !config.component) {
      console.error('[PLUGIN-SYSTEM] Invalid content override config:', {
        componentName,
        config,
      });
      return;
    }

    if (!pluginRegistry.contentOverrides[componentName]) {
      pluginRegistry.contentOverrides[componentName] = [];
    }

    // Mark component as raw to prevent Vue from making it reactive
    const safeConfig = { ...config, component: markRaw(config.component) };
    pluginRegistry.contentOverrides[componentName].push(safeConfig);
    log('✅ Content override registered:', componentName, config.tabKey);
  },

  getContentOverride(componentName: string, tabKey: string, context: any = {}): any {
    const overrides = pluginRegistry.contentOverrides[componentName] || [];

    const channelType = context.channel_type || context.inbox?.channel_type;
    
    // Extract provider from context
    const provider = context.provider ||
                    context.channel?.provider ||
                    context.channel?.additional_attributes?.provider ||
                    context.additional_attributes?.provider ||
                    context.inbox?.provider ||
                    context.inbox?.additional_attributes?.provider ||
                    context.inbox?.channel?.provider ||
                    context.inbox?.channel?.additional_attributes?.provider;

    // Find matching override
    const override = overrides.find(rep => {
      // Must match tab key
      if (rep.tabKey !== tabKey) return false;
      
      // If override has channelType filter, check if it matches
      if (rep.channelType && channelType !== rep.channelType) {
        return false;
      }
      
      // If override has provider filter, check if it matches
      if (rep.provider && provider !== rep.provider) {
        return false;
      }
      
      // If override has custom condition, evaluate it
      if (rep.condition && typeof rep.condition === 'function') {
        try {
          if (!rep.condition(context)) return false;
        } catch (error) {
          console.error('[PLUGIN-SYSTEM] Content override condition error:', error);
          return false;
        }
      }

      return true;
    });

    return override?.component || null;
  }, 

  executeHook(hookName: string, data: any): any {
    const hooks = pluginRegistry.hooks[hookName] || [];
    let result = data;

    hooks.forEach((callback: (...args: any[]) => any) => {
      try {
        const hookResult = callback(result);
        if (hookResult !== undefined) {
          result = hookResult;
        }
      } catch (error) {
        console.error(`[PLUGIN-SYSTEM] Hook error in ${hookName}:`, error);
      }
    });

    return result;
  },

  dispatchSocketEvent(
    eventName: string,
    data: any,
    context: Record<string, any> = {}
  ): any {
    if (!eventName) {
      return data;
    }

    return this.runSocketEvent(eventName, {
      event: eventName,
      data,
      ...context,
    });
  },

  runSocketEvent(eventName: string, payload: any): any {
    if (!eventName) {
      return payload;
    }

    return this.executeHook(socketHookName(eventName), payload);
  },

  executeActionCableEvent(eventName: string, payload: any): any {
    return this.runSocketEvent(eventName, payload);
  },

  hideTab(componentName: string, tabKey: string, options?: { condition?: (context: any) => boolean }): void {
    if (!componentName || !tabKey) {
      console.error('[PLUGIN-SYSTEM] Invalid hideTab params:', { componentName, tabKey });
      return;
    }

    const rule: TabVisibilityRule = {
      componentName,
      tabKeys: [tabKey],
      mode: 'hide',
      condition: options?.condition,
    };

    pluginRegistry.tabVisibility.push(rule);
    log('✅ Tab hidden:', componentName, tabKey);
  },

  showTab(componentName: string, tabKey: string, options?: { condition?: (context: any) => boolean }): void {
    if (!componentName || !tabKey) {
      console.error('[PLUGIN-SYSTEM] Invalid showTab params:', { componentName, tabKey });
      return;
    }

    const rule: TabVisibilityRule = {
      componentName,
      tabKeys: [tabKey],
      mode: 'show',
      condition: options?.condition,
    };

    pluginRegistry.tabVisibility.push(rule);
    log('✅ Tab shown:', componentName, tabKey);
  },

  showOnlyTabs(componentName: string, tabKeys: string[], options?: { condition?: (context: any) => boolean }): void {
    if (!componentName || !tabKeys || !Array.isArray(tabKeys)) {
      console.error('[PLUGIN-SYSTEM] Invalid showOnlyTabs params:', { componentName, tabKeys });
      return;
    }

    const rule: TabVisibilityRule = {
      componentName,
      tabKeys,
      mode: 'show-only',
      condition: options?.condition,
    };

    pluginRegistry.tabVisibility.push(rule);
    log('✅ Show-only tabs:', componentName, tabKeys.join(', '));
  },

  getVisibleTabs(componentName: string, context: any): TabConfig[] {
    const resolver = getTabResolver();
    
    // Get default tabs - for now return empty array as default
    // Components should provide their own default tabs
    const defaultTabs: TabConfig[] = [];
    
    return resolver.resolve(componentName, defaultTabs, context);
  },

  getTabContent(componentName: string, tabKey: string, context: any): any {
    const resolver = getTabResolver();
    return resolver.getContentForTab(componentName, tabKey, context);
  },

  getRegistry(): any {
    return getReadonlyRegistry();
  },

  clear(): void {
    clearRegistry();
    log('🗑️  Registry cleared');
  },
};
